import type { AssetStorageMode } from '../types/assetStorage';
import type { CalendarProject } from '../types/calendarProject';

const projectKeyPrefix = 'calendar_studio.project.';
const lastProjectKey = 'calendar_studio.last_project_id';

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function setLastProjectId(id: string) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(lastProjectKey, id);
}

export function saveProject(project: CalendarProject) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(`${projectKeyPrefix}${project.id}`, JSON.stringify(project));
  setLastProjectId(project.id);
}

export function loadProject(id: string): CalendarProject | null {
  if (!canUseStorage()) return null;

  const rawProject = window.localStorage.getItem(`${projectKeyPrefix}${id}`);
  if (!rawProject) return null;

  try {
    return JSON.parse(rawProject) as CalendarProject;
  } catch {
    return null;
  }
}

export function loadLastProject(): CalendarProject | null {
  if (!canUseStorage()) return null;

  const lastProjectId = window.localStorage.getItem(lastProjectKey);
  return lastProjectId ? loadProject(lastProjectId) : null;
}


const productNames: Record<CalendarProject['productId'], string> = {
  basic_year_story: 'Basic Year Story',
  family_planner_cz: 'Family Planner CZ',
  fineart_gallery: 'FineArt Gallery',
};

export interface CalendarProjectSummary {
  id: string;
  productId: CalendarProject['productId'];
  productName: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  pageCount: number;
  photoCount: number;
  selectedPhotoCount: number;
  preflightStatus: CalendarProject['preflight']['status'];
  assetStorageMode: AssetStorageMode;
  localAssetCount: number;
  cloudAssetCount: number;
  hasWarnings: boolean;
  hasErrors: boolean;
}

function createProjectId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `project_${crypto.randomUUID()}`;
  }

  return `project_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function fallbackProjectTitle(project: CalendarProject) {
  const productName = productNames[project.productId];
  return project.title?.trim() || `${productName} ${project.period.year}`;
}

function summarizeProject(project: CalendarProject): CalendarProjectSummary {
  const issues = project.preflight.lastReport?.checks.flatMap((check) => check.issues) ?? project.preflight.issues;
  return {
    id: project.id,
    productId: project.productId,
    productName: productNames[project.productId],
    title: fallbackProjectTitle(project),
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    pageCount: project.pages.length,
    photoCount: project.photos.length,
    selectedPhotoCount: project.photos.filter((photo) => photo.selectedForDraft).length,
    preflightStatus: project.preflight.status,
    assetStorageMode: project.photos.find((photo) => photo.assetStorage)?.assetStorage ?? 'indexeddb-local-mvp',
    localAssetCount: project.photos.filter((photo) => photo.localAssetKey || photo.hasLocalAsset).length,
    cloudAssetCount: project.photos.filter((photo) => photo.cloudAsset?.objectKey || photo.uploadStatus === 'uploaded').length,
    hasWarnings: issues.some((issue) => issue.severity === 'warning') || project.preflight.status === 'warning',
    hasErrors: issues.some((issue) => issue.severity === 'error') || project.preflight.status === 'error',
  };
}

export function listProjects(): CalendarProjectSummary[] {
  if (!canUseStorage()) return [];

  const projects: CalendarProject[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(projectKeyPrefix)) continue;
    const projectId = key.slice(projectKeyPrefix.length);
    const project = loadProject(projectId);
    if (project) projects.push(project);
  }

  return projects
    .sort((first, second) => new Date(second.updatedAt).getTime() - new Date(first.updatedAt).getTime())
    .map(summarizeProject);
}

export function deleteProject(projectId: string) {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(`${projectKeyPrefix}${projectId}`);
  if (window.localStorage.getItem(lastProjectKey) === projectId) {
    window.localStorage.removeItem(lastProjectKey);
  }
}

export function duplicateProject(projectId: string): CalendarProject | null {
  const sourceProject = loadProject(projectId);
  if (!sourceProject) return null;

  const now = new Date().toISOString();
  const title = `${fallbackProjectTitle(sourceProject)} kopie`;
  const duplicatedProject: CalendarProject = {
    ...sourceProject,
    id: createProjectId(),
    title,
    createdAt: now,
    updatedAt: now,
    photos: sourceProject.photos.map((photo) => ({ ...photo, assignedPageId: photo.assignedPageId })),
    pages: sourceProject.pages.map((page) => ({ ...page })),
    preflight: {
      ...sourceProject.preflight,
      issues: [...sourceProject.preflight.issues],
      lastReport: sourceProject.preflight.lastReport ? { ...sourceProject.preflight.lastReport } : undefined,
    },
  };

  saveProject(duplicatedProject);
  return duplicatedProject;
}

export function renameProject(projectId: string, newName: string): CalendarProject | null {
  const trimmedName = newName.trim();
  if (!trimmedName) return null;

  const project = loadProject(projectId);
  if (!project) return null;

  const updatedProject = {
    ...project,
    title: trimmedName,
    updatedAt: new Date().toISOString(),
  } satisfies CalendarProject;
  saveProject(updatedProject);
  return updatedProject;
}
